import styles from './index.scss';
import { Dropdown, DropdownItem } from 'design-system-web';
import React, { useMemo, useCallback } from 'react';
import {
  OnboardingCompanyResult,
  Edge as CompanyEdge,
} from 'lane-shared/contexts/SignUpContext/useOnboardingCompanies';
import {
  ChannelSearchCircleListWrapper,
  convertCompanyEdgeToChannelType,
} from 'components/onboarding/components/ChannelSearchCircleListWrapper/ChannelSearchCircleListWrapper';
import type { Channel } from 'application-types';

export type OnboardingCompanyInputProps = {
  data: {
    isReadOnly: boolean;
    signupError: Error | null;
    building: Channel | null;
    company: Channel | null;
    onboardingCompaniesResult: OnboardingCompanyResult;
  };
  handlers: {
    resetSelection: () => void;
    onParentCompanySelectionChange: (parentCompany: Channel) => void;
    renderSelectedLocation: (company: CompanyEdge) => React.ReactNode;
  };
};

export function OnboardingCompanyDropDownInput({
  data: {
    isReadOnly,
    company,
    onboardingCompaniesResult: {
      companies,
      isInitialLoading: isCompanyInitialLoading,
    },
  },
  handlers: {
    resetSelection,
    onParentCompanySelectionChange,
    renderSelectedLocation,
  },
}: OnboardingCompanyInputProps) {
  // Convert companies to DropdownItem format
  const dropdownItems = useMemo<DropdownItem<CompanyEdge>[]>(
    () =>
      companies.map(c => ({
        label: c.node.name, // Required by react-select
        value: c,
      })),
    [companies]
  );

  // Find selected item in dropdown format
  const selectedDropdownItem = useMemo<DropdownItem<CompanyEdge> | null>(() => {
    if (!company) {
      return null;
    }

    const selectedCompanyEdge = companies.find(c => c.node.id === company._id);

    if (!selectedCompanyEdge) {
      return null;
    }

    return {
      label: selectedCompanyEdge.node.name,
      value: selectedCompanyEdge,
    };
  }, [company, companies]);

  // Handle selection change
  const handleDropdownChange = useCallback(
    (item: DropdownItem<CompanyEdge> | null) => {
      if (item) {
        const companyChannel = convertCompanyEdgeToChannelType(item.value);

        onParentCompanySelectionChange(companyChannel);
      } else {
        resetSelection();
      }
    },
    [onParentCompanySelectionChange, resetSelection]
  );

  // Load options - triggers search when user types
  const loadOptions = useCallback(
    async (inputValue: string): Promise<DropdownItem<CompanyEdge>[]> => {
      // Update search text - this triggers the search query
      // onParentCompanySearchTextChange(inputValue); //TODO: Uncomment this once we have a proper search with infinite scroll

      // Filter companies based on input value (case-insensitive)
      const filteredCompanies = inputValue
        ? companies.filter(c =>
            c.node.name.toLowerCase().includes(inputValue.toLowerCase())
          )
        : companies;

      // Return filtered companies as dropdown items
      return filteredCompanies.map(c => ({
        label: c.node.name,
        value: c,
      }));
    },
    [companies]
  );

  // Custom option rendering - adapts renderItem to renderOption
  const renderOption = useCallback(
    (props: any) => {
      const companyItem = props.data.value;
      const isSelected = company?._id === companyItem.node.id;

      return (
        <ChannelSearchCircleListWrapper
          company={companyItem}
          onClick={_company => {
            props.selectOption(props.data);
          }}
          className={styles.companyResult}
          isSelected={Boolean(company && isSelected)}
          hideDescription
        />
      );
    },
    [company]
  );

  // Custom selected value rendering
  const renderSelectedValue = useCallback(
    (props: any) => {
      if (!props.data) return null;

      const companyItem = props.data.value;

      return renderSelectedLocation(companyItem);
    },
    [renderSelectedLocation]
  );

  return (
    <div className={styles.searchAndResultsWrapper}>
      <div>
        <Dropdown<CompanyEdge>
          value={selectedDropdownItem}
          onChange={handleDropdownChange}
          loadOptions={loadOptions}
          defaultOptions={dropdownItems}
          renderOption={renderOption}
          renderSelectedValue={renderSelectedValue}
          isLoading={isCompanyInitialLoading}
          placeholder="onboarding.swire.location.companyLocationPlaceholder"
          isSearchable
          disabled={isReadOnly}
          testId="companyName"
          className={isReadOnly ? styles.disabledDropdown : ''}
        />
      </div>
    </div>
  );
}
